import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authAPI, sessionAPI, hiitAPI } from '../../services/api';
import { syncPendingWorkouts } from '../../utils/nativeStorage';
import apiCache from '../../utils/apiCache';
import toast from 'react-hot-toast';
import i18n from '../../i18n';
import { Capacitor } from '@capacitor/core';

// Module-level flag: prevents biometric auto-login right after an explicit logout.
// Resets on app restart (module reload) so biometric auto-login works on cold start.
let _justLoggedOut = false;
export function didJustLogOut() {
  if (_justLoggedOut) {
    _justLoggedOut = false;
    return true;
  }
  return false;
}

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue, dispatch }) => {
    try {
      // Clear ALL cached data before login to prevent cross-user data leakage
      apiCache.clearAll();
      
      const response = await authAPI.login(email, password);
      
      // Store token in localStorage
      localStorage.setItem('token', response.data.token);
      
      const successMessage = i18n.isInitialized ? i18n.t('auth:messages.loginSuccess') : 'Login successful!';
      toast.success(successMessage);

      // Flush any workouts queued while the member was logged out / their token
      // was expired. Fire-and-forget; the token was just stored above.
      syncPendingWorkouts(
        (sid, d) => sessionAPI.completeSession(sid, d),
        (sid, d) => hiitAPI.updateSession(sid, d)
      ).catch(() => {});

      // Offer biometric login on native platforms (non-blocking)
      if (Capacitor.isNativePlatform()) {
        try {
          const { isBiometricAvailable, isBiometricEnabled, saveBiometricCredentials, setBiometricPrompted, wasBiometricPrompted, getBiometricLabel } = await import('../../utils/biometricAuth');
          const { isAvailable, biometryType } = await isBiometricAvailable();
          if (isAvailable && !wasBiometricPrompted()) {
            setBiometricPrompted();
            const label = getBiometricLabel(biometryType);
            // Show styled modal via Redux instead of window.confirm()
            dispatch(authSlice.actions.showBiometricPrompt({ label, email, password }));
          } else if (isAvailable && isBiometricEnabled()) {
            saveBiometricCredentials(email, password);
          }
        } catch (bioErr) {
          console.warn('Biometric setup skipped:', bioErr);
        }
      }

      return response.data;
    } catch (error) {
      console.error('❌ [Frontend Login] Error:', error.response?.status, error.response?.data);
      let message = error.response?.data?.message || (i18n.isInitialized ? i18n.t('auth:errors.loginFailed') : 'Login failed');
      const status = error.response?.data?.status;
      
      if (status === 'payment_pending') {
        message = i18n.isInitialized 
          ? i18n.t('auth:errors.trainerPaymentPending') 
          : 'Please complete your registration payment to continue.';
      } else if (status === 'pending') {
        message = i18n.isInitialized 
          ? i18n.t('auth:errors.trainerPendingApproval') 
          : 'Your account is pending admin approval. You will receive an email once approved.';
      } else if (status === 'rejected') {
        message = i18n.isInitialized 
          ? i18n.t('auth:errors.trainerRejected') 
          : 'Your trainer registration has been rejected. Please contact support.';
      }
      
      // Translate specific backend errors
      if (i18n.isInitialized && message.includes('User already exists')) {
        message = i18n.t('auth:errors.userAlreadyExists');
      }
      
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async ({ userType, userData }, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userType, userData);
      
      // Store token in localStorage
      localStorage.setItem('token', response.data.token);
      
      // Check if payment is required
      if (response.data.paymentRequired) {
        const paymentMessage = i18n.isInitialized 
          ? i18n.t('auth:messages.registrationPaymentRequired') 
          : 'Registration successful! Please complete payment.';
        toast.success(paymentMessage);
      } else {
        const successMessage = i18n.isInitialized ? i18n.t('auth:messages.registrationSuccess') : 'Registration successful!';
        toast.success(successMessage);
      }
      
      return response.data;
    } catch (error) {
      let message = error.response?.data?.message || (i18n.isInitialized ? i18n.t('auth:errors.registrationFailed') : 'Registration failed');
      
      // Translate specific backend errors
      if (i18n.isInitialized) {
        if (message.includes('User already exists')) {
          message = i18n.t('auth:errors.userAlreadyExists');
        } else if (message.includes('Trainer not found')) {
          message = i18n.t('auth:errors.trainerNotFound');
        }
      }
      
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
if (!token) {
        return rejectWithValue('No token found');
      }
const response = await authAPI.verifyToken();
return response.data;
    } catch (error) {
localStorage.removeItem('token');
      return rejectWithValue('Token verification failed');
    }
  }
);

/**
 * Perform async cleanup after logout (push unregister).
 * Biometric credentials are intentionally preserved so biometric login
 * works on the next session. They are only removed when the user
 * explicitly disables biometrics or when a login attempt fails.
 */
function performLogoutCleanup() {
  const pushToken = localStorage.getItem('push_device_token');

  setTimeout(async () => {
    if (pushToken) {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (Capacitor.isNativePlatform()) {
          await authAPI.unregisterDeviceToken({ token: pushToken }).catch(() => {});
          const { cleanupNativePush } = await import('../../utils/nativePushNotifications');
          await cleanupNativePush();
        }
      } catch (_) { /* ignore */ }
    }
  }, 0);

  localStorage.removeItem('push_device_token');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  apiCache.clearAll();
}

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: localStorage.getItem('token'),
    isAuthenticated: false,
    isLoading: false,
    error: null,
    isInitialized: false,
    paymentRequired: false,
    registrationFee: null,
    biometricPrompt: null, // { label, email, password } when awaiting user decision
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearPaymentRequired: (state) => {
      state.paymentRequired = false;
      state.registrationFee = null;
    },
    updateProfile: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    setInitialized: (state) => {
      state.isInitialized = true;
    },
    logoutImmediate: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = null;
      state.isLoading = false;
      state.biometricPrompt = null;
    },
    showBiometricPrompt: (state, action) => {
      state.biometricPrompt = action.payload; // { label, email, password }
    },
    dismissBiometricPrompt: (state) => {
      state.biometricPrompt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload;
      })
      
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.paymentRequired = false;
        state.registrationFee = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = null;
        state.paymentRequired = action.payload.paymentRequired || false;
        state.registrationFee = action.payload.registrationFee || null;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload;
        state.paymentRequired = false;
        state.registrationFee = null;
      })
      
      // Verify Token
      .addCase(verifyToken.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.error = null;
        state.isInitialized = true;
      })
      .addCase(verifyToken.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;
        state.isInitialized = true;
      });
  },
});

export const { clearError, updateProfile, setInitialized, clearPaymentRequired, logoutImmediate, showBiometricPrompt, dismissBiometricPrompt } = authSlice.actions;

/**
 * Dispatch this to log out. Clears state synchronously, then runs async
 * native cleanup (push, biometric) in the background so the UI never stalls.
 */
export const logout = () => (dispatch) => {
  _justLoggedOut = true;
  performLogoutCleanup();
  dispatch(logoutImmediate());
  const successMessage = i18n.isInitialized ? i18n.t('common:toast.success.loggedOut') : 'Logged out successfully';
  toast.success(successMessage);
};

// Selectors
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;
export const selectUserRole = (state) => state.auth.user?.role;
export const selectIsInitialized = (state) => state.auth.isInitialized;
export const selectPaymentRequired = (state) => state.auth.paymentRequired;
export const selectRegistrationFee = (state) => state.auth.registrationFee;
export const selectBiometricPrompt = (state) => state.auth.biometricPrompt;

export default authSlice.reducer;